import React, { useEffect, useState, lazy, Suspense } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BankOutlined } from '@ant-design/icons';
import axiosInstance from '../../utils/axios';
import PageHeading from '../../components/PageHeading';
import SectionForm from './SectionForm';
import { Alert,message } from 'antd';

const SeatPreview = lazy(() => import('./SeatPreview'));

const ScreenTool = () => {
  const { screenId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    is_active: false,
    sections: [],
  });
  const [seatTypes, setSeatTypes] = useState([]);
  const [seatConfigs, setSeatConfigs] = useState({});

  useEffect(() => {
    fetchScreen();
    fetchSeatTypes();
  }, []);

const fetchScreen = async () => {
  setLoading(true);
  try {
    const { data } = await axiosInstance.get(`theatre-management/screen/${screenId}/`);

    const sectionsWithIndex = (data.sections || []).map((section, sectionIdx) => {
      // Sort blocks ascending by block_order
      const sortedBlocks = (section.blocks || [])
        .slice()
        .sort((a, b) => (a.block_order || 0) - (b.block_order || 0));

      return {
        ...section,
        index: sectionIdx,
        hide: section.hide || false,
        blocks: sortedBlocks.map((block, blockIdx) => {
          // Sort rows ASCENDING by row_order (A=1 first → front)
          const sortedRows = (block.rows || [])
            .slice()
            .sort((a, b) => (a.row_order || 0) - (b.row_order || 0));

          return {
            ...block,
            index: blockIdx,
            order: block.block_order,
            rows: sortedRows.map((row, sortedRowIdx) => ({
              ...row,
              sortedIndex: sortedRowIdx,  // Stable index for seat keys
              order: row.row_order,
              total_seats: row.seats?.length || 0,
              seats: (row.seats || [])
                .slice()
                .sort((a, b) => {
                  const posA = a.position ?? a.number ?? 0;
                  const posB = b.position ?? b.number ?? 0;
                  return posA - posB;
                }),
            })),
          };
        }),
      };
    });

    // Build seatConfigs using STABLE sorted indices
    const seatConfigsObj = {};
    sectionsWithIndex.forEach((section, sectionIdx) => {
      section.blocks.forEach((block, blockIdx) => {
        block.rows.forEach((row) => {
          const rowIdx = row.sortedIndex;  // Use stable sorted index
          row.seats.forEach((seat, seatIdx) => {
            const key = `${sectionIdx}_${blockIdx}_${rowIdx}_${seatIdx}`;
            console.log("CREATE KEY:", key, {
              section: section.name,
              block: block.name,
              row: row.name,
              seat: seat.number,
            });
            seatConfigsObj[key] = {
              name: seat.name || '',
              number: seat.number,
              position: seat.position,
              status: seat.status,
              seat_type: seat.seat_type?.id || null,
              suffix: seat.suffix || '',
              merge_to_left: seat.merge_to_left || false,
              merge_to_right: seat.merge_to_right || false,
            };
          });
        });
      });
    });

    setFormData({
      name: data.name,
      is_active: data.is_active,
      sections: sectionsWithIndex,
    });

    setSeatConfigs(seatConfigsObj);
  } catch (err) {
    console.error(err);
    alert('Failed to fetch screen');
  } finally {
    setLoading(false);
  }
};

  const fetchSeatTypes = async () => {
    try {
      const { data } = await axiosInstance.get(`core/seat-type/`);
      setSeatTypes(data);
    } catch (err) {
      alert('Failed to fetch seat types');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSectionsChange = (newSections) => {
    setFormData((prev) => ({
      ...prev,
      sections: newSections,
    }));
  };

  const addIndices = (sections) => {
    return sections.map((section, sectionIdx) => ({
      ...section,
      index: sectionIdx,
      blocks: (section.blocks || []).map((block, blockIdx) => ({
        ...block,
        index: blockIdx,
        rows: (block.rows || []).map((row, rowIdx) => ({
          ...row,
          index: rowIdx,
        })),
      })),
    }));
  };

  // VALIDATION ON SUBMIT
  const validateLayout = () => {
    const sections = formData.sections;

    // Check screen name
    if (!formData.name?.trim()) {
      message.error('Screen name is required');
      return false;
    }

    // Check sections
    if (sections.length === 0) {
      message.error('At least one section is required');
      return false;
    }

    // Check unique section names
    const sectionNames = sections.map(s => s.name?.trim().toLowerCase());
    const duplicateSections = sectionNames.filter((name, idx) => name && sectionNames.indexOf(name) !== idx);
    if (duplicateSections.length > 0) {
      message.error('Section names must be unique');
      return false;
    }

    for (const section of sections) {
      if (!section.name?.trim()) {
        message.error('All sections must have a name');
        return false;
      }
      if (!section.price || section.price <= 0) {
        message.error(`Section "${section.name}" must have a valid price`);
        return false;
      }

      if (!section.blocks || section.blocks.length === 0) {
        message.error(`Section "${section.name}" must have at least one block`);
        return false;
      }

      // Check unique block names in section
      const blockNames = section.blocks.map(b => b.name?.trim().toLowerCase());
      const duplicateBlocks = blockNames.filter((name, idx) => name && blockNames.indexOf(name) !== idx);
      if (duplicateBlocks.length > 0) {
        message.error(`Block names must be unique in section "${section.name}"`);
        return false;
      }

      for (const block of section.blocks) {
        if (!block.name?.trim()) {
          message.error(`All blocks in section "${section.name}" must have a name`);
          return false;
        }

        if (!block.rows || block.rows.length === 0) {
          message.error(`Block "${block.name}" must have at least one row`);
          return false;
        }

        // Check unique row names in block
        const rowNames = block.rows.map(r => r.name?.trim().toLowerCase());
        const duplicateRows = rowNames.filter((name, idx) => name && rowNames.indexOf(name) !== idx);
        if (duplicateRows.length > 0) {
          message.error(`Row names must be unique in block "${block.name}"`);
          return false;
        }

        for (const row of block.rows) {
          if (!row.name?.trim()) {
            message.error(`All rows in block "${block.name}" must have a name`);
            return false;
          }
          if (!row.total_seats || row.total_seats <= 0) {
            message.error(`Row "${row.name}" must have at least 1 seat`);
            return false;
          }
        }
      }
    }

    return true;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateLayout()) {
      return;
    }
    const payload = {
      ...formData,
      sections: addIndices(formData.sections || []),
      seat_configs: seatConfigs,
    };

    try {
      await axiosInstance.patch(`theatre-management/screen/create/${screenId}/`, payload);
      alert('Screen layout updated');
      navigate(-1);
    } catch (err) {
      console.error(err);
      alert('Failed to update layout');
    }
  };

  return (
    <div className="dashboard">
      <PageHeading name="Seating Management" icon={<BankOutlined />} />
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          <div>
            <label htmlFor="name">Screen Name</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>
          <div>
            <label>
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
              />{' '}
              Is Active
            </label>
          </div>
        </div>

        <h4 style={{ marginTop: '30px' }}>Seat Sections</h4>
        <SectionForm
          value={formData.sections}
          onChange={handleSectionsChange}
        />

        <h4 style={{ marginTop: '30px' }}>Seating Preview</h4>
        <Alert
          message="Very important"
          description="Do not change seat configuration if booking is on going for these seats"
          type="warning"
        />
        <div style={{ padding: 20, border: '1px dashed #ccc' }}>
          <Suspense fallback={<div>Loading preview...</div>}>
            <SeatPreview
              sections={formData.sections}
              seatTypes={seatTypes}
              seatConfigs={seatConfigs}
              setSeatConfigs={setSeatConfigs}
            />
          </Suspense>
        </div>

        <br />
        <button type="submit" disabled={loading}>
          {loading ? 'Saving...' : 'Submit'}
        </button>
      </form>
    </div>
  );
};

export default ScreenTool;
